import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'

export interface EligibilitySearchForm {
  companyUuid?: string
  companySearch?: string
  fullTextAddress?: string
  codeBatiment?: string
}

interface EligibilityResult {
  id?: string
  uuid: string
  address: string
  buildingCode?: string
  isEligible: boolean
  result: any
  fulladdress: string
  operators?: string[]
  technologies?: string[]
  createdAt: string
  companyUuid?: string
  companyName?: string
  companySiret?: string
  dateCreated: string
  company: {
    siret: string
    name: string
  }
  authorUuid?: string
  author?: {
    uuid: string
    firstName?: string | null
    lastName?: string | null
    email: string
  }
}

export const useEligibilityStore = defineStore('eligibility', () => {
  const suiteStore = useSuiteStore()

  // État pour les résultats de recherche
  const searchResults = ref<EligibilityResult | null>(null)
  const isSearching = ref(false)

  // État pour l'historique
  const eligibilityHistory = ref<EligibilityResult[]>([])
  const isLoadingHistory = ref(false)

  // État pour une éligibilité spécifique
  const currentEligibility = ref<EligibilityResult | null>(null)
  const isLoadingDetails = ref(false)

  /**
   * Vérifie l'éligibilité d'une adresse
   */
  const checkEligibility = async (searchForm: EligibilitySearchForm) => {
    isSearching.value = true
    try {
      const response = await suiteStore.suiteFetch('/eligibility/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(searchForm),
        credentials: 'include',
      })

      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        searchResults.value = data
        return data
      }
      if (response && typeof response !== 'boolean' && !response.ok) {
        let message = "Erreur lors de la vérification d'éligibilité"
        try {
          const body = await response.json()
          const raw = body?.message ?? body?.error ?? message
          message = Array.isArray(raw) ? raw.join(', ') : String(raw)
        } catch {
          message = (await response.text().catch(() => '')) || message
        }
        throw new Error(message)
      }
    } catch (error) {
      console.error("Erreur lors de la vérification d'éligibilité:", error)
      throw error
    } finally {
      isSearching.value = false
    }
    return null
  }

  /**
   * Récupère l'historique des éligibilités
   */
  const fetchEligibilityHistory = async (filters: { search?: string; companyUuid?: string; offset?: number; limit?: number } = {}) => {
    isLoadingHistory.value = true
    try {
      const response = await suiteStore.suiteFetch('/eligibility', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filters),
        credentials: 'include',
      })

      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        eligibilityHistory.value = Array.isArray(data) ? data : (data?.data ?? [])
        return eligibilityHistory.value
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique:", error)
    } finally {
      isLoadingHistory.value = false
    }
    return []
  }

  /**
   * Récupère les détails d'une éligibilité spécifique
   */
  const fetchEligibilityDetails = async (eligibilityId: string) => {
    isLoadingDetails.value = true
    try {
      const response = await suiteStore.suiteFetch(`eligibility/${eligibilityId}/detail`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })

      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        currentEligibility.value = data
        return data
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des détails:', error)
    } finally {
      isLoadingDetails.value = false
    }
    return null
  }

  /**
   * Exporte les données d'éligibilité au format CSV
   */
  const exportToCsv = async (
    eligibilityId?: string,
    filters?: { commitmentMonths?: number; technology?: string; provider?: string; bandwidthMbps?: number },
  ) => {
    try {
      const params = new URLSearchParams()
      if (filters?.commitmentMonths != null) params.set('commitmentMonths', String(filters.commitmentMonths))
      if (filters?.technology) params.set('technology', filters.technology)
      if (filters?.provider) params.set('provider', filters.provider)
      if (filters?.bandwidthMbps != null) params.set('bandwidthMbps', String(filters.bandwidthMbps))
      const qs = params.toString()
      const endpoint = `eligibility/${eligibilityId}/export${qs ? `?${qs}` : ''}`

      const response = await suiteStore.suiteFetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/csv',
        },
        credentials: 'include',
      })

      if (response && typeof response !== 'boolean' && response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `eligibility-${eligibilityId || 'export'}-${new Date().toISOString()}.csv`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        return true
      }
    } catch (error) {
      console.error("Erreur lors de l'export CSV:", error)
      return false
    }
    return false
  }

  /**
   * Réinitialise les résultats de recherche
   */
  const resetSearchResults = () => {
    searchResults.value = null
  }

  const searchOdooCompanies = async (q: string, limit = 20) => {
    const params = new URLSearchParams({ q, limit: String(limit) })
    const response = await suiteStore.suiteFetch(`companies/search/odoo?${params}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (response && typeof response !== 'boolean' && response.ok) {
      const data = await response.json()
      return data?.data ?? []
    }
    return []
  }

  const syncOdooPartner = async (odooPartnerId: number) => {
    const response = await suiteStore.suiteFetch('companies/sync/odoo-partner', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ odooPartnerId }),
      credentials: 'include',
    })
    if (response && typeof response !== 'boolean' && response.ok) {
      return await response.json()
    }
    throw new Error('Erreur lors de la synchronisation')
  }

  return {
    // État
    searchResults,
    isSearching,
    eligibilityHistory,
    isLoadingHistory,
    currentEligibility,
    isLoadingDetails,

    // Méthodes
    checkEligibility,
    fetchEligibilityHistory,
    fetchEligibilityDetails,
    exportToCsv,
    resetSearchResults,
    searchOdooCompanies,
    syncOdooPartner,
  }
})
